import type {
  ActivityDTO, CollaboratorRole, CsvModule, DashboardDTO, EffectiveAccess, InsightsDTO, InviteDTO, MemberDTO, PaymentScheduleDTO, PaymentSource, Plan,
  PrintableDTO, PrintableKind, UpdateMemberBody, UpdateWeddingBody, WeddingDTO,
} from "@bridewell/shared";
import { paymentBucket, paymentTotals, sortPayments } from "@bridewell/shared";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl, http } from "./client";
import { keys, RESOURCE } from "./keys";

/* One wedding: the row and what this person may do in it, its derived pages, its settings, its people, its lifecycle. */

/** `ownerPlan`: the wedding owner's tier (caps and wedding perks bind to it). Fails closed to free. UI hint only — the API decides. */
export interface WeddingWithAccess { wedding: WeddingDTO; access: EffectiveAccess; ownerPlan: Plan }
export const weddingQuery = (id: string) =>
  queryOptions({
    queryKey: keys.wedding(id),
    queryFn: async () => {
      const res = await http.get<WeddingDTO>(`/weddings/${id}`);
      const ownerPlan = (res.meta?.ownerPlan as Plan | undefined) ?? "free";
      return { wedding: res.data, access: res.access as EffectiveAccess, ownerPlan } satisfies WeddingWithAccess;
    },
  });

export const dashboardQuery = (id: string) =>
  queryOptions({ queryKey: keys.dashboard(id), queryFn: async () => (await http.get<DashboardDTO>(`/weddings/${id}/dashboard`)).data, staleTime: 10_000 });

export const activityQuery = (id: string) =>
  queryOptions({ queryKey: keys.activity(id), queryFn: async () => (await http.get<ActivityDTO[]>(`/weddings/${id}/activity`)).data });

export const insightsQuery = (id: string) =>
  queryOptions({ queryKey: keys.insights(id), queryFn: async () => (await http.get<InsightsDTO>(`/weddings/${id}/insights`)).data, staleTime: 15_000 });

/** Budget lines + vendor instalments, bucketed by the server (iOS PaymentSchedule rules). */
export const paymentsQuery = (id: string) =>
  queryOptions({ queryKey: keys.payments(id), queryFn: async () => (await http.get<PaymentScheduleDTO>(`/weddings/${id}/payments`)).data, staleTime: 10_000 });

/** Tick a payment paid/unpaid. Optimistic: the row moves bucket at once; the server's answer replaces it on settle. */
export function useSetPaymentPaid(weddingId: string) {
  const qc = useQueryClient();
  const key = keys.payments(weddingId);
  return useMutation({
    mutationFn: async ({ source, sourceId, paid }: { source: PaymentSource; sourceId: string; paid: boolean }) => {
      await http.post(`/weddings/${weddingId}/payments/${source}/${sourceId}/paid`, { paid });
    },
    onMutate: async ({ source, sourceId, paid }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PaymentScheduleDTO>(key);
      if (prev) {
        const now = new Date();
        const items = sortPayments(prev.items.map((p) =>
          p.source === source && p.sourceId === sourceId ? { ...p, paid, bucket: paymentBucket(new Date(p.dueDate), paid, now) } : p));
        qc.setQueryData<PaymentScheduleDTO>(key, { ...prev, items, totals: paymentTotals(items, now, prev.totals.paid !== null) });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: keys.module(weddingId, RESOURCE.budget) });
      void qc.invalidateQueries({ queryKey: keys.module(weddingId, RESOURCE.vendors) });
      void qc.invalidateQueries({ queryKey: keys.dashboard(weddingId) });
      void qc.invalidateQueries({ queryKey: keys.activity(weddingId) });
    },
  });
}

/* ───────── exports + printables ───────── */

/** Download URLs on the API origin (cookie auth on the web). Opened by the platform's downloader — components never fetch. */
export const exportUrl = {
  csv: (weddingId: string, module: CsvModule) => apiUrl(`/weddings/${weddingId}/export/${module}.csv?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`),
  json: (weddingId: string) => apiUrl(`/weddings/${weddingId}/export.json`),
  ics: (weddingId: string) => apiUrl(`/weddings/${weddingId}/export/timeline.ics`),
  /** A getter: read after `configureApi`, not at import time. */
  get account() { return apiUrl("/me/export"); },
};

export const printableQuery = (weddingId: string, kind: PrintableKind) =>
  queryOptions({
    queryKey: keys.printable(weddingId, kind),
    queryFn: async () => (await http.get<PrintableDTO>(`/weddings/${weddingId}/export/printables/${kind}`)).data,
    retry: false,
  });

/* ───────── settings + lifecycle ───────── */

/**
 * Wedding details, budget total + currency, category targets (wedding core: owner or co-owner; the server checks). The
 * names and date feed the switcher list, the date and budget the dashboard, the currency every amount on the payments page.
 */
export function useUpdateWedding(weddingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: UpdateWeddingBody) => (await http.patch<WeddingDTO>(`/weddings/${weddingId}`, patch)).data,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.wedding(weddingId) });
      await qc.invalidateQueries({ queryKey: keys.myWeddings });
      void qc.invalidateQueries({ queryKey: keys.dashboard(weddingId) });
      void qc.invalidateQueries({ queryKey: keys.payments(weddingId) });
    },
  });
}

/**
 * Archive / unarchive / hand-off change the wedding row, the access shape and the switcher list; delete (owner) and
 * leave (everyone else) end this person's access. After `remove` or `leave`, navigate away first and only then drop the
 * wedding's cache (`qc.removeQueries({ queryKey: keys.wedding(id) })`): the open screens still read it until the route changes.
 */
export function useWeddingLifecycle(weddingId: string) {
  const qc = useQueryClient();
  const after = async () => {
    await qc.invalidateQueries({ queryKey: keys.wedding(weddingId) });
    await qc.invalidateQueries({ queryKey: keys.myWeddings });
    await qc.invalidateQueries({ queryKey: keys.usage });
    await qc.invalidateQueries({ queryKey: keys.planner });
  };
  const gone = async () => {
    await qc.invalidateQueries({ queryKey: keys.myWeddings });
    await qc.invalidateQueries({ queryKey: keys.usage });
  };
  const archive = useMutation({ mutationFn: async () => (await http.post<WeddingDTO>(`/weddings/${weddingId}/archive`)).data, onSuccess: after });
  const unarchive = useMutation({ mutationFn: async () => (await http.post<WeddingDTO>(`/weddings/${weddingId}/unarchive`)).data, onSuccess: after });
  const transfer = useMutation({
    mutationFn: async (toMemberId: string) => (await http.post<WeddingDTO>(`/weddings/${weddingId}/transfer`, { toMemberId })).data,
    onSuccess: async () => { await after(); await qc.invalidateQueries({ queryKey: keys.members(weddingId) }); },
  });
  const remove = useMutation({ mutationFn: async () => { await http.del(`/weddings/${weddingId}`); }, onSuccess: gone });
  const leave = useMutation({ mutationFn: async () => { await http.post(`/weddings/${weddingId}/leave`); }, onSuccess: gone });
  return { archive, unarchive, transfer, remove, leave };
}

/* ───────── people ───────── */

export const membersQuery = (id: string) =>
  queryOptions({ queryKey: keys.members(id), queryFn: async () => (await http.get<MemberDTO[]>(`/weddings/${id}/members`)).data });
export const invitesQuery = (id: string) =>
  queryOptions({ queryKey: keys.invites(id), queryFn: async () => (await http.get<InviteDTO[]>(`/weddings/${id}/invites`)).data });

/**
 * Everyone in a wedding. `rename` is each person's own display name (every role); the rest is the owner's people
 * management: a member's role, grants and money access (applied per change), removing and restoring a member, and
 * invite links (`invite` returns the new link; link = access).
 */
export function usePeople(weddingId: string) {
  const qc = useQueryClient();
  const members = () => qc.invalidateQueries({ queryKey: keys.members(weddingId) });
  const invites = () => qc.invalidateQueries({ queryKey: keys.invites(weddingId) });
  return {
    rename: useMutation({ mutationFn: async (displayName: string) => { await http.patch(`/weddings/${weddingId}/members/me`, { displayName }); }, onSuccess: members }),
    update: useMutation({
      mutationFn: async ({ memberId, patch }: { memberId: string; patch: UpdateMemberBody }) => { await http.patch(`/weddings/${weddingId}/members/${memberId}`, patch); },
      onSuccess: members,
    }),
    revoke: useMutation({ mutationFn: async (memberId: string) => { await http.post(`/weddings/${weddingId}/members/${memberId}/revoke`); }, onSuccess: members }),
    restore: useMutation({ mutationFn: async (memberId: string) => { await http.post(`/weddings/${weddingId}/members/${memberId}/restore`); }, onSuccess: members }),
    invite: useMutation({ mutationFn: async (role: CollaboratorRole) => (await http.post<InviteDTO>(`/weddings/${weddingId}/invites`, { role })).data, onSuccess: invites }),
    revokeInvite: useMutation({ mutationFn: async (token: string) => { await http.del(`/weddings/${weddingId}/invites/${token}`); }, onSuccess: invites }),
  };
}
